import { Global } from "@emotion/core";
import { ThemeKey, Token } from "@smartsheet/design-tokens";
import React, { ComponentType, HTMLAttributes } from "react";

export interface TokenInjectorProps<SupportedThemes extends ThemeKey> extends HTMLAttributes<HTMLElement> {
    themeKey: SupportedThemes;
}

export interface CreateTokenInjectorResult<SupportedThemes extends ThemeKey, TokenNames> {
    Injector: ComponentType<TokenInjectorProps<SupportedThemes>>;
    getToken: (name: TokenNames, fallbackValue?: string) => string;
}

export interface CreateTokenInjectorArgs<
    SupportedThemes extends ThemeKey,
    TokenArr extends ReadonlyArray<Token<SupportedThemes>>
> {
    /**
     * A unique string (among all custom injectors) that helps avoid collisions in token names.
     * Since tokens are injected as CSS custom properties, they are all global.
     */
    namespace: string;

    /**
     * This is an array of the themes your injector supports. It cannot include random theme
     * strings - it must be a subset of ThemeKey from @smartsheet/design-tokens.
     */
    supportedThemes: SupportedThemes[];

    /**
     * The set of Tokens to include in this injector.
     */
    tokens: TokenArr;
}

export function createTokenInjector<
    SupportedThemes extends ThemeKey,
    TokenArr extends ReadonlyArray<Token<SupportedThemes>>
>({
    tokens,
    namespace,
    supportedThemes,
}: CreateTokenInjectorArgs<SupportedThemes, TokenArr>): CreateTokenInjectorResult<
    SupportedThemes,
    TokenArr[number]["name"]
> {
    if (process.env.NODE_ENV !== "production") {
        const invalidNameCharacters = /[^\w-]/;
        if (invalidNameCharacters.test(namespace)) {
            console.warn(
                `createTokenInjector: the namespace "${namespace}" can only contain characters allowed in css custom properties.`
            );
        }
        const set = new Set<string>();
        for (const { name } of tokens) {
            if (set.has(name)) {
                console.warn(`createTokenInjector: duplicate token name found: "${name}".`);
            }
            if (invalidNameCharacters.test(name)) {
                console.warn(
                    `createTokenInjector: the token "${name}" can only contain characters allowed in css custom properties.`
                );
            }
            set.add(name);
        }
    }

    // Create 2 things:
    // 1) a set of css custom properties to pass along to the injector, keyed by the supported
    //    theme names.
    const cssCustomPropertiesByThemeKey = {} as Record<SupportedThemes, Record<string, string | number>>;
    // 2) a dictionary of token names => the CSS custom property names from #1.
    const tokenNameLookup = {} as Record<string, string>;

    for (const themeKey of supportedThemes) {
        cssCustomPropertiesByThemeKey[themeKey] = {} as Record<string, string>;
    }

    for (const token of tokens) {
        const tokenVariableName = `--smar--${namespace}--${token.name}`;
        // insert the variable name into the by-token-name lookup table.
        tokenNameLookup[token.name] = tokenVariableName;
        for (const [themeName, tokenValue] of Object.entries(token.value as Record<string, string | number>)) {
            // If the token doesn't have a value, skip it. This means it doesn't get injected into
            // the DOM later and will allow custom properties to cascade properly.
            // Ex:
            // var(--some-property, "fallback-value") only works if --some-property is not defined.
            if (tokenValue) {
                // Insert the css custom property into the map for the DOM.
                const thisThemesVariables = cssCustomPropertiesByThemeKey[themeName as SupportedThemes] as Record<
                    string,
                    string | number
                >;
                thisThemesVariables[tokenVariableName] = tokenValue;
            }
        }
    }
    return {
        getToken: (name, fallbackValue) => `var(${tokenNameLookup[name]}${fallbackValue ? `, ${fallbackValue}` : ""})`,
        Injector: ({ themeKey }: TokenInjectorProps<SupportedThemes>) => {
            return (
                <Global
                    styles={{
                        ":root": cssCustomPropertiesByThemeKey[themeKey],
                    }}
                />
            );
        },
    };
}
